import React, { useCallback } from "react"
import { useTranslation } from "react-i18next"

import { useObservableState } from "observable-hooks"
import { interval } from "rxjs"

import { parseDuration, ShorthandDuration } from "@treefort/lib/parse-duration"
import icons from "@treefort/tokens/app/icons"

import { useTrack } from "../hooks/audio"
import { useBooleanState } from "../hooks/use-boolean-state"
import { Track } from "../lib/audio-player"
import { debug as appDebug } from "../lib/logging"
import {
  isExpired,
  PlaybackTimer,
  playbackTimerObservable,
  playbackTimerStore,
} from "../lib/playback-timer"
import { DurationInputModal } from "./duration-input-modal"
import IconButton, { IconButtonProps } from "./icon-button"
import Select from "./select"

const debug = appDebug.extend("playback-timer-select")

const PRESET_PLAYBACK_TIMER_DURATION_MINUTES = [5, 15, 30, 60, 90]

const PLAYBACK_TIMER_SELECT_MAX_WIDTH_PX = 280

type TimerDurationValue = "off" | "end" | "custom" | ShorthandDuration

type PlaybackTimerDurationSelectorProps = Omit<
  IconButtonProps,
  "id" | "source" | "label"
>

type Translator = ReturnType<typeof useTranslation>["t"]

function getTimerIconLabel(t: Translator, activeTimer?: PlaybackTimer | null) {
  if (!activeTimer) {
    return ""
  }

  switch (activeTimer.type) {
    case "milestone":
      return t("End")
    case "time": {
      const remainingMs =
        activeTimer.createdAt +
        parseDuration(activeTimer.duration) -
        new Date().getTime()

      const seconds = remainingMs / 1000
      const minutes = Math.round(seconds / 60)
      return seconds < 60 ? "<1m" : `${minutes}m`
    }
    default:
      return ""
  }
}

function getEndTimerLabel(t: Translator, track: Track | null) {
  if (!track) {
    return ""
  }

  switch (track.extra.contentType) {
    case "album":
      return t("End of track")
    case "book":
      return t("End of chapter")
    case "video":
      return t("End of video")
    case "podcast":
      return t("End of episode")
  }
}

export function PlaybackTimerSelect(props: PlaybackTimerDurationSelectorProps) {
  const [timerOptionsOpen, openTimerOptions, closeTimerOptions] =
    useBooleanState(false)
  const activeTimer = useObservableState(playbackTimerObservable)
  const refreshInterval = useObservableState(() => interval(1000))

  const { t } = useTranslation()
  const track = useTrack()

  const [isCustomDurationOpen, openCustomDuration, closeCustomDuration] =
    useBooleanState(false)

  const timerOffOption = { label: t("Off"), value: "off" as const }
  const endOfChapterOption = {
    label: getEndTimerLabel(t, track),
    value: "end" as const,
  }

  const getCustomDurationLabel = () => {
    if (!activeTimer || activeTimer.type !== "time") {
      return ""
    }

    const minutes = parseDuration(activeTimer.duration) / 1000 / 60

    if (PRESET_PLAYBACK_TIMER_DURATION_MINUTES.includes(minutes)) {
      return ""
    }

    return `(${t("{{number}} minutes", { number: minutes })})`
  }

  const timerDurationOptions: {
    label: string
    value: TimerDurationValue
  }[] = [
    timerOffOption,
    ...PRESET_PLAYBACK_TIMER_DURATION_MINUTES.map((number) => ({
      label: t("{{number}} minutes", { number }),
      value: `${number}m` as const,
    })),
    endOfChapterOption,
    {
      label: `${t("Custom")} ${getCustomDurationLabel()}`,
      value: "custom" as const,
    },
  ]

  const getSelectTimerSelectedValue = (timer: PlaybackTimer | null) => {
    const presetDuration = timerDurationOptions.find((opt) =>
      timer?.type !== "time" ? undefined : opt.value === timer.duration,
    )

    switch (timer?.type) {
      case "milestone":
        return "end"
      case "time":
        return (
          presetDuration?.value ||
          timerDurationOptions.find((opt) => opt.value === "custom")?.value ||
          "off"
        )
      default:
        return "off"
    }
  }

  const getTimerLabel = useCallback(
    () => (isExpired(activeTimer) ? "" : getTimerIconLabel(t, activeTimer)),
    // We want to invalidate this callback on refreshInterval
    // so the label updates when a timer expires
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [t, activeTimer, refreshInterval],
  )

  const onSelectTimerOption = useCallback(
    (selectedValue: TimerDurationValue) => {
      debug("Selected value", { selectedValue })
      closeTimerOptions()

      switch (selectedValue) {
        case "off":
          debug("Clearing timer")
          void playbackTimerStore.set(null)
          break
        case "end":
          debug("Setting timer for chapter end")
          void playbackTimerStore.set({
            type: "milestone",
            milestone: "endOfChapter",
          })
          break
        case "custom":
          debug("Showing modal to select custom duration")
          openCustomDuration()
          break
        default:
          debug(`Setting duration to ${selectedValue}`)
          void playbackTimerStore.set({
            type: "time",
            duration: selectedValue,
          })
      }
    },
    [closeTimerOptions, openCustomDuration],
  )

  return (
    <>
      <IconButton
        {...props}
        id="audio-player-controls-audio-timer"
        onPress={openTimerOptions}
        source={icons.stopwatch}
        label={getTimerLabel()}
        showLabel={true}
        labelPosition="overlaid"
        state={activeTimer && !isExpired(activeTimer) ? "active" : "default"}
      />
      <Select
        id="audio-player-controls-playback-timer-duration-selector"
        label={t("Timer")}
        open={timerOptionsOpen}
        onClose={closeTimerOptions}
        value={getSelectTimerSelectedValue(activeTimer)}
        onChange={onSelectTimerOption}
        options={timerDurationOptions}
        maxWidth={PLAYBACK_TIMER_SELECT_MAX_WIDTH_PX}
      />
      <DurationInputModal
        /* Remount when timer changes to reset the default value in the text input */
        key={`duration-input-${activeTimer?.createdAt || 0}`}
        label={t("Custom timer")}
        open={isCustomDurationOpen}
        valueMinutes={
          activeTimer && activeTimer.type === "time"
            ? parseDuration(activeTimer.duration) / 1000 / 60
            : undefined
        }
        onClose={closeCustomDuration}
        onChange={(duration) =>
          playbackTimerStore.set({ type: "time", duration })
        }
      />
    </>
  )
}
